/**
 * Status badges for verifications, bookings, listings, disputes and sex.
 *
 * Labels / colours come from our own maps keyed by the seeded IDs (RidesConstants); unknown
 * IDs fall back to the API's name / colour so a new lookup row still renders.
 */

import type { ReactNode } from 'react';
import {
  IconHourglassHigh,
  IconShieldCheck,
  IconShieldX,
  IconClockX,
  IconBan,
  IconCircleCheck,
  IconCircleX,
  IconUserOff,
  IconDoorEnter,
  IconDoorExit,
  IconFlag,
  IconCar,
  IconUsers,
  IconGenderMale,
  IconGenderFemale,
  IconEye,
  IconChecks,
  IconUpload,
  IconClockHour4,
  IconCalendarX,
  IconTrash,
} from '@tabler/icons-react';
import { Badge, type ThemedBadgeProps } from '@/components/ui';
import { humanize, RIDES_SEX } from './ridesUtils';

const ICON = 13;

export interface StatusMeta {
  label: string;
  color: string;
  icon?: ReactNode;
}

export const VERIFICATION_STATUS_META: Record<number, StatusMeta> = {
  1: { label: 'Pending', color: 'yellow', icon: <IconHourglassHigh size={ICON} /> },
  2: { label: 'Verified', color: 'green', icon: <IconShieldCheck size={ICON} /> },
  3: { label: 'Failed', color: 'red', icon: <IconShieldX size={ICON} /> },
  4: { label: 'Expired', color: 'orange', icon: <IconClockX size={ICON} /> },
  5: { label: 'Revoked', color: 'dark', icon: <IconBan size={ICON} /> },
};

export const BOOKING_STATUS_META: Record<number, StatusMeta> = {
  1: { label: 'Confirmed', color: 'green', icon: <IconCircleCheck size={ICON} /> },
  2: { label: 'Cancelled by rider', color: 'gray', icon: <IconCircleX size={ICON} /> },
  3: { label: 'Cancelled by driver', color: 'orange', icon: <IconCircleX size={ICON} /> },
  4: { label: 'Completed', color: 'blue', icon: <IconChecks size={ICON} /> },
  5: { label: 'No-show', color: 'red', icon: <IconUserOff size={ICON} /> },
};

export const AVAILABILITY_STATUS_META: Record<number, StatusMeta> = {
  1: { label: 'Open', color: 'green', icon: <IconDoorEnter size={ICON} /> },
  2: { label: 'Full', color: 'blue', icon: <IconUsers size={ICON} /> },
  3: { label: 'Closed', color: 'gray', icon: <IconDoorExit size={ICON} /> },
  4: { label: 'Arrived', color: 'teal', icon: <IconCar size={ICON} /> },
  5: { label: 'Cancelled', color: 'red', icon: <IconCircleX size={ICON} /> },
  6: { label: 'Completed', color: 'dark', icon: <IconChecks size={ICON} /> },
};

export const DISPUTE_STATUS_META: Record<number, StatusMeta> = {
  1: { label: 'Open', color: 'red', icon: <IconFlag size={ICON} /> },
  2: { label: 'Under review', color: 'yellow', icon: <IconEye size={ICON} /> },
  3: { label: 'Resolved', color: 'green', icon: <IconCircleCheck size={ICON} /> },
  4: { label: 'Dismissed', color: 'gray', icon: <IconBan size={ICON} /> },
};

/** RideUploadStatus (254g) — uploaded document copies. */
export const UPLOAD_STATUS_META: Record<number, StatusMeta> = {
  1: { label: 'Uploading', color: 'gray', icon: <IconUpload size={ICON} /> },
  2: { label: 'Waiting for review', color: 'orange', icon: <IconClockHour4 size={ICON} /> },
  3: { label: 'Accepted', color: 'green', icon: <IconCircleCheck size={ICON} /> },
  4: { label: 'Rejected', color: 'red', icon: <IconCircleX size={ICON} /> },
  5: { label: 'Expired', color: 'orange', icon: <IconCalendarX size={ICON} /> },
  6: { label: 'Withdrawn', color: 'gray', icon: <IconTrash size={ICON} /> },
};

/** RideRequirementLevel (254g) — how strongly a document / field is asked for. */
export const REQUIREMENT_LEVEL_META: Record<number, { label: string; color: string; hint: string }> = {
  1: { label: 'Not asked', color: 'gray', hint: 'Hidden from members' },
  2: { label: 'Optional', color: 'blue', hint: 'Shown; members may skip it' },
  3: { label: 'Required', color: 'red', hint: 'Must be provided before they can continue' },
};

/** SegmentedControl data for a requirement level (values are the seeded IDs as strings). */
export const REQUIREMENT_LEVEL_OPTIONS = [1, 2, 3].map((id) => ({ value: String(id), label: REQUIREMENT_LEVEL_META[id].label }));

export const verificationStatusLabel = (id: number | null | undefined, name?: string | null) =>
  (id != null && VERIFICATION_STATUS_META[id]?.label) || humanize(name) || (id ? `Status ${id}` : 'Not started');
export const bookingStatusLabel = (id: number | null | undefined, name?: string | null) =>
  (id != null && BOOKING_STATUS_META[id]?.label) || humanize(name) || (id ? `Status ${id}` : 'Unknown');
export const availabilityStatusLabel = (id: number | null | undefined, name?: string | null) =>
  (id != null && AVAILABILITY_STATUS_META[id]?.label) || humanize(name) || (id ? `Status ${id}` : 'Unknown');
export const disputeStatusLabel = (id: number | null | undefined, name?: string | null) =>
  (id != null && DISPUTE_STATUS_META[id]?.label) || humanize(name) || (id ? `Status ${id}` : 'Unknown');
export const uploadStatusLabel = (id: number | null | undefined, name?: string | null) =>
  (id != null && UPLOAD_STATUS_META[id]?.label) || humanize(name) || (id ? `Status ${id}` : 'Unknown');

type BadgeBase = Omit<ThemedBadgeProps, 'children' | 'color'>;

interface StatusBadgeProps extends BadgeBase {
  statusId?: number | null;
  name?: string | null;
  /** API colour (hex) — only used for IDs we do not know. */
  color?: string | null;
  withIcon?: boolean;
}

const baseStyles = { root: { textTransform: 'none' as const, fontWeight: 600, letterSpacing: 0 } };

function metaBadge(meta: Record<number, StatusMeta>, label: string, { statusId, color, size = 'md', withIcon = true, ...rest }: StatusBadgeProps) {
  const m = statusId != null ? meta[statusId] : undefined;
  return (
    <Badge size={size} variant="light" color={m?.color || color || 'gray'} leftSection={withIcon ? m?.icon : undefined} styles={baseStyles} {...rest}>
      {label}
    </Badge>
  );
}

export function VerificationBadge(props: StatusBadgeProps) {
  return metaBadge(VERIFICATION_STATUS_META, verificationStatusLabel(props.statusId, props.name), props);
}

export function BookingStatusBadge(props: StatusBadgeProps) {
  return metaBadge(BOOKING_STATUS_META, bookingStatusLabel(props.statusId, props.name), props);
}

export function AvailabilityStatusBadge(props: StatusBadgeProps) {
  return metaBadge(AVAILABILITY_STATUS_META, availabilityStatusLabel(props.statusId, props.name), props);
}

export function DisputeStatusBadge(props: StatusBadgeProps) {
  return metaBadge(DISPUTE_STATUS_META, disputeStatusLabel(props.statusId, props.name), props);
}

export function UploadStatusBadge(props: StatusBadgeProps) {
  return metaBadge(UPLOAD_STATUS_META, uploadStatusLabel(props.statusId, props.name), props);
}

interface SexBadgeProps extends BadgeBase {
  sexId?: number | null;
  name?: string | null;
}

/** Male / Female — the matching rule hinges on it, so it is always visible on driver rows. */
export function SexBadge({ sexId, name, size = 'sm', ...rest }: SexBadgeProps) {
  const female = sexId === RIDES_SEX.Female;
  const male = sexId === RIDES_SEX.Male;
  return (
    <Badge
      size={size}
      variant="outline"
      color={female ? 'pink' : male ? 'blue' : 'gray'}
      leftSection={female ? <IconGenderFemale size={ICON} /> : male ? <IconGenderMale size={ICON} /> : undefined}
      styles={baseStyles}
      {...rest}
    >
      {female ? 'Female' : male ? 'Male' : humanize(name) || 'Not set'}
    </Badge>
  );
}

/** Compact yes / no pill ("ID ✓", "Licence ✗"). */
export function CheckBadge({ ok, label, size = 'sm', ...rest }: BadgeBase & { ok: boolean; label: string }) {
  return (
    <Badge
      size={size}
      variant="light"
      color={ok ? 'green' : 'red'}
      leftSection={ok ? <IconShieldCheck size={ICON} /> : <IconShieldX size={ICON} />}
      styles={baseStyles}
      {...rest}
    >
      {label}
    </Badge>
  );
}
